import React, { useEffect, useRef, useState } from 'react'

import { faBars, faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { matchPath } from 'react-router'
import { Link } from 'react-router-dom'

type NavItem = {
  path: string
  label: string
  subItems?: NavItem[]
}

const useStyles = makeStyles((theme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#023560',
    color: 'white',
    minWidth: 280,
    overflowY: 'auto',
    borderRadius: '0 0 4px 4px',
    padding: 0,
    '& .MuiListItem-root.MuiListItem-button.Mui-selected, .MuiListItem-root.Mui-selected': {
      backgroundColor: '#15769a',
      textDecoration: 'none',
    },
  },
  subList: {
    backgroundColor: '#022d51',
  },
  item: {
    borderTop: '1px solid #15769a',
  },
  subItem: {
    paddingLeft: theme.spacing(4),
    borderTop: '1px solid #15769a',
  },
}))

export default function ResponsiveNav({ menuItems }: { menuItems: NavItem[] }) {
  const classes = useStyles()
  const theme = useTheme()
  //   Using bootstrap breakpoints for media query while the navigation is still using bootstrap
  const isSmallScreenSize = useMediaQuery(theme.breakpoints.down(768))
  const [open, setOpen] = useState(false)
  const [subItemsOpen, setSubItemsOpen] = useState<string>()
  const anchorRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    for (const item of menuItems) {
      if (window.location.pathname.includes(item.path)) {
        setSubItemsOpen(item.label)
        break
      }
    }
  }, [menuItems])

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  const handleSubNavigation = (item: NavItem) => {
    if (item.subItems) {
      setSubItemsOpen(item.label)
      return
    }
    setOpen(false)
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus()
    }

    prevOpen.current = open
  }, [open])

  if (!isSmallScreenSize) {
    return null
  }

  return (
    <Box>
      <Button
        ref={anchorRef}
        aria-controls={open ? 'nav-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Box
          display="flex"
          alignItems="center"
          gridGap={4}
          color="white"
        >
          <FontAwesomeIcon
            size="sm"
            icon={faBars}
          />
          Menu
        </Box>
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <List
              dense
              id="nav-menu"
              className={classes.list}
            >
              {menuItems.map((item) => {
                const match = matchPath(window.location.pathname, {
                  path: item.path,
                })
                return (
                  <React.Fragment key={item.label}>
                    <ListItem
                      button
                      component={item.subItems ? 'div' : Link}
                      to={item.path}
                      className={classes.item}
                      onClick={() => handleSubNavigation(item)}
                      selected={Boolean(match)}
                    >
                      <ListItemText>{item.label}</ListItemText>
                      {item.subItems ? (
                        <FontAwesomeIcon
                          icon={subItemsOpen === item.label ? faChevronUp : faChevronDown}
                          size="xs"
                        />
                      ) : null}
                    </ListItem>
                    {item.subItems ? (
                      <Collapse
                        in={subItemsOpen === item.label}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List
                          dense
                          className={classes.subList}
                        >
                          {item.subItems.map((subItem: NavItem) => {
                            const subMatch = matchPath(window.location.pathname, {
                              path: subItem.path,
                            })
                            return (
                              <ListItem
                                component={Link}
                                to={subItem.path}
                                key={subItem.label}
                                className={classes.subItem}
                                onClick={() => setOpen(false)}
                                selected={Boolean(subMatch)}
                              >
                                <ListItemText>{subItem.label}</ListItemText>
                              </ListItem>
                            )
                          })}
                        </List>
                      </Collapse>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </List>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </Box>
  )
}
